import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Pola tabel-responsif — dipakai ULANG di halaman manapun yang punya tabel
 * data padat yang sering dibuka lewat HP (staf gudang, kasir keliling, dst).
 * DUA rendering paralel dari SATU sumber data: `<table>` penuh di layar
 * `>= md`, kartu bertumpuk di layar `< md`. Keduanya ada di DOM sekaligus
 * (disembunyikan lewat CSS `hidden` / `md:hidden`), BUKAN dipilih lewat
 * deteksi lebar layar di JS — lebih sederhana, tidak "loncat" saat resize
 * jendela, dan tetap benar saat print / SSR.
 *
 * KENAPA testid kartu != testid baris tabel: elemen interaktif yang tampil
 * DUA KALI (mis. tombol "Terima" di kartu & di baris tabel untuk PO yang
 * sama) tidak boleh berbagi `data-testid` — query getByTestId/exact match
 * akan menabrak dua node sekaligus walau salah satunya CSS-hidden. Konvensi
 * di seluruh app: testid tabel DIPERTAHANKAN apa adanya (test lama
 * bergantung padanya), versi kartu memakai testid baru — kalau elemennya
 * juga berupa tombol aksi yang persis sama, tambahkan akhiran `-m`
 * (`inv-po-terima-${id}` di tabel -> `inv-po-terima-${id}-m` di kartu).
 *
 * Contoh pemakaian (lihat InventoryPO.jsx untuk versi lengkap dengan 4 tabel):
 *
 *   <TabelDesktop>
 *     <table className="w-full text-sm">
 *       <thead>...</thead>
 *       <tbody className="divide-y divide-slate-100">
 *         <LoadingState loading={loading} error={error} kosong={!loading && items.length === 0}
 *           onRetry={load} varian="tabel" kolom={6} data-testid="x-state">
 *           {items.map(it => <tr key={it.id}>...</tr>)}
 *         </LoadingState>
 *       </tbody>
 *     </table>
 *   </TabelDesktop>
 *
 *   <DaftarKartuMobile testid="x-kartu">
 *     <LoadingState loading={loading} error={error} kosong={!loading && items.length === 0}
 *       onRetry={load} varian="kartu" data-testid="x-state-m">
 *       {items.map(it => (
 *         <KartuBaris key={it.id} testid={`x-card-${it.id}`}>
 *           <KartuBarisHeader
 *             kiri={<><p className="font-mono text-[11px] text-slate-500">{it.kode}</p>
 *                     <p className="text-sm font-semibold text-slate-900">{it.judul}</p></>}
 *             kanan={<StatusBadge status={it.status} />}
 *           />
 *           <KartuBarisMeta label="Gudang" value={it.warehouse_nama} />
 *           <KartuBarisMeta label="Nilai" value={idr(it.nilai_total)} tebal />
 *           <KartuBarisAksi>
 *             <button data-testid={`x-terima-${it.id}-m`}>Terima</button>
 *           </KartuBarisAksi>
 *         </KartuBaris>
 *       ))}
 *     </LoadingState>
 *   </DaftarKartuMobile>
 */

/** Wrapper tabel — hanya tampil di layar `>= md`. */
export function TabelDesktop({ children, className }) {
  return <div className={cn("hidden md:block overflow-x-auto", className)}>{children}</div>;
}

/** Wrapper daftar kartu — hanya tampil di layar `< md`. Jarak, permukaan,
 *  dan garis kartu mengikuti tema workspace bersama. */
export function DaftarKartuMobile({ children, className, testid }) {
  return (
    <div data-testid={testid} className={cn("eh-card-list md:hidden", className)}>
      {children}
    </div>
  );
}

/** Satu kartu (setara satu baris tabel). Padding & background konsisten
 *  dengan konvensi kartu app (`bg-white`); border/rounded diatur tema bersama. */
export function KartuBaris({ children, testid, className, onClick }) {
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={cn("eh-row-card p-4 space-y-2 bg-white", onClick && "cursor-pointer active:bg-slate-50", className)}
    >
      {children}
    </div>
  );
}

/** Baris atas kartu: identitas (kode/judul/subjudul) di kiri, status atau
 *  nilai paling penting di kanan — persis padanan kolom pertama & terakhir
 *  yang paling sering dilihat di tabel aslinya. */
export function KartuBarisHeader({ kiri, kanan, className }) {
  return (
    <div className={cn("flex items-start justify-between gap-3", className)}>
      <div className="min-w-0 flex-1">{kiri}</div>
      {kanan != null && <div className="shrink-0 text-right">{kanan}</div>}
    </div>
  );
}

/** Satu baris label:value — padanan satu kolom sekunder tabel. Dilewati
 *  otomatis kalau `value` kosong, supaya kartu tidak penuh baris "-". */
export function KartuBarisMeta({ label, value, tebal = false, className }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className={cn("eh-card-meta flex items-center justify-between gap-2 text-xs", className)}>
      <span className="text-slate-500">{label}</span>
      <span className={cn("text-slate-800 text-right", tebal && "font-mono font-semibold text-slate-900")}>
        {value}
      </span>
    </div>
  );
}

/** Baris aksi (tombol) di bagian bawah kartu — rata kanan, boleh melipat ke
 *  baris baru kalau tombolnya banyak (PO menunggu approval bisa punya 2-3). */
export function KartuBarisAksi({ children, className }) {
  return <div className={cn("eh-card-actions flex items-center justify-end gap-1.5 flex-wrap pt-1", className)}>{children}</div>;
}
